import type { FC } from "react";
import { useMemo } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Workspace } from "@src/core/services";
import { APP_ROUTES } from "@src/core/constants";
import {
  BreadcrumbsContainer,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbCurrent,
} from "./breadcrumbs.styles";

interface BreadcrumbItem {
  label: string;
  path?: string;
}

export const Breadcrumbs: FC = () => {
  const { pathname } = useLocation();
  const { workspaceId, routeId, logId } = useParams<{
    workspaceId: string;
    routeId: string;
    logId: string;
  }>();

  const { data: workspacesData } = useQuery({
    queryKey: ["workspaces"],
    queryFn: () => Workspace.getAll(),
    enabled: !!workspaceId,
  });

  const workspaceName = useMemo(() => {
    if (!workspaceId || !workspacesData?.data.items) return null;
    const ws = workspacesData.data.items.find(
      (item) => item.id === Number(workspaceId)
    );
    return ws?.name ?? `#${workspaceId}`;
  }, [workspaceId, workspacesData]);

  const items = useMemo((): BreadcrumbItem[] => {
    const crumbs: BreadcrumbItem[] = [
      { label: "Панель управления", path: APP_ROUTES.DASHBOARD },
    ];

    if (pathname.startsWith(APP_ROUTES.WORKSPACE)) {
      crumbs.push({ label: "Пространства", path: APP_ROUTES.WORKSPACE });

      if (pathname.includes("/create") && !workspaceId) {
        crumbs.push({ label: "Создание" });
      } else if (pathname.includes("/update/") && !pathname.includes("/routes/")) {
        crumbs.push({ label: "Редактирование" });
      } else if (workspaceId) {
        crumbs.push({
          label: workspaceName ?? `#${workspaceId}`,
          path: `${APP_ROUTES.WORKSPACE}/${workspaceId}`,
        });

        if (pathname.includes("/routes")) {
          if (pathname.includes("/create")) {
            crumbs.push({ label: "Создание маршрута" });
          } else if (pathname.includes("/update/") && routeId) {
            crumbs.push({ label: `Маршрут #${routeId}` });
          }
        }
      }
    } else if (pathname === APP_ROUTES.LOGS) {
      crumbs.push({ label: "Журнал запросов" });
    } else if (pathname.startsWith(APP_ROUTES.SETTINGS)) {
      crumbs.push({ label: "Настройки" });
    } else if (pathname.startsWith(APP_ROUTES.CONNECT)) {
      crumbs.push({ label: "Агенты (MCP)" });
    } else if (pathname.startsWith(APP_ROUTES.LOG) && logId) {
      crumbs.push({ label: `Лог #${logId}` });
    } else if (pathname === APP_ROUTES.DASHBOARD) {
      return [{ label: "Панель управления" }];
    }

    return crumbs;
  }, [pathname, workspaceId, workspaceName, routeId, logId]);

  if (items.length <= 1) return null;

  return (
    <BreadcrumbsContainer>
      {items.map((item, index) => {
        const isLast = index === items.length - 1;

        if (isLast) {
          return <BreadcrumbCurrent key={item.label}>{item.label}</BreadcrumbCurrent>;
        }

        return (
          <span key={item.label} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <BreadcrumbLink>
              {item.path ? (
                <Link to={item.path}>{item.label}</Link>
              ) : (
                item.label
              )}
            </BreadcrumbLink>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
          </span>
        );
      })}
    </BreadcrumbsContainer>
  );
};
